import { createApi } from '@reduxjs/toolkit/query/react';
import fetchBaseQueryCSRF from '../helper';
import {
  SearchTermsFilters,
  SearchTermsGridResponse,
  OpportunityCardsResponse,
  ClicksAdCostsResponse,
  VisibilityResponse,
  TopCompetitorsResponse,
  RegionsResponse,
  CompetitorsResponse,
} from './models';
import {
  PaginateQuery,
  CompetitorsFiltersQuery,
  SearchTermsFiltersQuery,
  SearchTermsFiltersQueryWithSearchTerms,
} from './query';
import { GridFilterModel } from '@mui/x-data-grid';

const nonBrandedAPI = createApi({
  baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL, credentials: 'include' }),
  reducerPath: 'api.nonBranded',
  tagTypes: ['nonBranded'],
  endpoints: (builder) => ({
    searchTermsFilters: builder.query<SearchTermsFilters, SearchTermsFiltersQuery>({
      query: (query) => ({
        url: '/non-branded/filters',
        method: 'GET',
        params: query,
      }),
    }),
    searchTermsGrid: builder.query<
      SearchTermsGridResponse,
      SearchTermsFiltersQueryWithSearchTerms &
      PaginateQuery & {
        filterModel?: GridFilterModel['items'];
        opportunity?: string | null;
        sort_field?: string;
        sort_order?: string;
        interval?: string;
        start_compare?: string;
        end_compare?: string;
        compare?: boolean;
      }
    >({
      query: (query) => {
        let url = '/non-branded/search_terms/grid';
        let params = [];
        if (query.countries && query.countries.length > 0) {
          params.push(
            `countries=${query.countries.map((country) => country.replace('&', '__et__')).join('&countries=')}`
          );
        }
        if (query.devices && query.devices.length > 0) {
          params.push(`devices=${query.devices.join('&devices=')}`);
        }
        if (query.platforms && query.platforms.length > 0) {
          params.push(`platforms=${query.platforms.join('&platforms=')}`);
        }
        if (query.search_terms && query.search_terms.length > 0) {
          params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
        }

        const limit = `&limit=${query.limit || 10}`;
        const page = `&page=${query.page || 0}`;
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        const start_date = query.start_date ? `&start_date=${query.start_date}` : '';
        const end_date = query.end_date ? `&end_date=${query.end_date}` : '';
        const filter_model = query.filterModel
          ? `&filter_model=${JSON.stringify(query.filterModel)}`
          : '';
        const opportunity = query.opportunity ? `&opportunity=${query.opportunity}` : '';
        const sort_field = query.sort_field
          ? `&sort_field=${query.sort_field}&sort_order=${query.sort_order}`
          : '';
        const start_compare = query.start_compare ? `&start_compare=${query.start_compare}` : '';
        const end_compare = query.end_compare ? `&end_compare=${query.end_compare}` : '';
        const compare = query.compare ? `&compare=${query.compare ? 'true' : 'false'}` : '&compare=false';
        return {
          url:
            url +
            '?' +
            params.join('&') +
            limit +
            page +
            workspace_id +
            start_date +
            end_date +
            filter_model +
            opportunity +
            sort_field +
            start_compare +
            end_compare +
            compare,
          method: 'GET',
        };
      },
    }),
    opportunityCards: builder.query<
      OpportunityCardsResponse,
      SearchTermsFiltersQueryWithSearchTerms
    >({
      query: (query) => {
        let url = '/non-branded/search_terms/opportunity_cards';
        let params = [];
        if (query.countries.length > 0) {
          params.push(
            `countries=${query.countries.map((country) => country.replace('&', '__et__')).join('&countries=')}`
          );
        }
        if (query.devices.length > 0) {
          params.push(`devices=${query.devices.join('&devices=')}`);
        }
        if (query.platforms.length > 0) {
          params.push(`platforms=${query.platforms.join('&platforms=')}`);
        }
        if (query.search_terms.length > 0) {
          params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
        }
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        const start_date = query.start_date ? `&start_date=${query.start_date}` : '';
        const end_date = query.end_date ? `&end_date=${query.end_date}` : '';
        return {
          url: url + '?' + params.join('&') + workspace_id + start_date + end_date,
          method: 'GET',
        };
      },
    }),
    clicksAdCosts: builder.query<
      ClicksAdCostsResponse,
      SearchTermsFiltersQueryWithSearchTerms & {
        interval?: string;
        compare?: boolean;
        compare_start_date?: string;
        compare_end_date?: string;
      }
    >({
      query: (query) => {
        let url = '/non-branded/search_terms/clicks_ad_costs' + (query.compare ? '/compare' : '');
        let params = [];
        if (query.countries.length > 0) {
          params.push(
            `countries=${query.countries.map((country) => country.replace('&', '__et__')).join('&countries=')}`
          );
        }
        if (query.devices.length > 0) {
          params.push(`devices=${query.devices.join('&devices=')}`);
        }
        if (query.platforms.length > 0) {
          params.push(`platforms=${query.platforms.join('&platforms=')}`);
        }
        if (query.search_terms.length > 0) {
          params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
        }
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        const start_date = query.start_date ? `&start_date=${query.start_date}` : '';
        const end_date = query.end_date ? `&end_date=${query.end_date}` : '';
        const interval = query.interval ? `&interval=${query.interval}` : '';
        const compare_start_date = query.compare_start_date
          ? `&compare_start_date=${query.compare_start_date}`
          : '';
        const compare_end_date = query.compare_end_date
          ? `&compare_end_date=${query.compare_end_date}`
          : '';
        return {
          url:
            url +
            '?' +
            params.join('&') +
            workspace_id +
            start_date +
            end_date +
            interval +
            compare_start_date +
            compare_end_date,
          method: 'GET',
        };
      },
    }),
    visibility: builder.query<
      VisibilityResponse,
      SearchTermsFiltersQueryWithSearchTerms & { interval?: string; compare?: boolean; compare_start_date?: string; compare_end_date?: string }
    >({
      query: (query) => {
        let url = '/non-branded/search_terms/visibility' + (query.compare ? '/compare' : '');
        let params = [];
        if (query.countries.length > 0) {
          params.push(
            `countries=${query.countries.map((country) => country.replace('&', '__et__')).join('&countries=')}`
          );
        }
        if (query.devices.length > 0) {
          params.push(`devices=${query.devices.join('&devices=')}`);
        }
        if (query.platforms.length > 0) {
          params.push(`platforms=${query.platforms.join('&platforms=')}`);
        }
        if (query.search_terms.length > 0) {
          params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
        }
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        const start_date = query.start_date ? `&start_date=${query.start_date}` : '';
        const end_date = query.end_date ? `&end_date=${query.end_date}` : '';
        const interval = query.interval ? `&interval=${query.interval}` : '';
        const compare_start_date = query.compare_start_date ? `&compare_start_date=${query.compare_start_date}` : '';
        const compare_end_date = query.compare_end_date ? `&compare_end_date=${query.compare_end_date}` : '';
        return {
          url: url + '?' + params.join('&') + workspace_id + start_date + end_date + interval + compare_start_date + compare_end_date,
          method: 'GET',
        };
      },
    }),
    topCompet: builder.query<
      TopCompetitorsResponse,
      SearchTermsFiltersQueryWithSearchTerms & { interval?: string }
    >({
      query: (query) => {
        let url = '/non-branded/search_terms/top_compet';
        let params = [];
        if (query.countries.length > 0) {
          params.push(
            `countries=${query.countries.map((country) => country.replace('&', '__et__')).join('&countries=')}`
          );
        }
        if (query.devices.length > 0) {
          params.push(`devices=${query.devices.join('&devices=')}`);
        }
        if (query.platforms.length > 0) {
          params.push(`platforms=${query.platforms.join('&platforms=')}`);
        }
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        const start_date = query.start_date ? `&start_date=${query.start_date}` : '';
        const end_date = query.end_date ? `&end_date=${query.end_date}` : '';
        const interval = query.interval ? `&interval=${query.interval}` : '';
        return {
          url: url + '?' + params.join('&') + workspace_id + start_date + end_date + interval,
          method: 'GET',
        };
      },
    }),
    regions: builder.query<RegionsResponse, SearchTermsFiltersQueryWithSearchTerms>({
      query: (query) => {
        let url = '/non-branded/search_terms/regions';
        let params = [];
        if (query.countries.length > 0) {
          params.push(
            `countries=${query.countries.map((country) => country.replace('&', '__et__')).join('&countries=')}`
          );
        }
        if (query.devices.length > 0) {
          params.push(`devices=${query.devices.join('&devices=')}`);
        }
        if (query.platforms.length > 0) {
          params.push(`platforms=${query.platforms.join('&platforms=')}`);
        }
        if (query.search_terms.length > 0) {
          params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
        }
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        const start_date = query.start_date ? `&start_date=${query.start_date}` : '';
        const end_date = query.end_date ? `&end_date=${query.end_date}` : '';
        return {
          url: url + '?' + params.join('&') + workspace_id + start_date + end_date,
          method: 'GET',
        };
      },
    }),
    competitors: builder.query<any, CompetitorsFiltersQuery & { query: string, domain_name?: string }>({
      query: ({ query, ...filters }) => {
        return {
          url: '/non-branded/competitors/' + query,
          method: 'GET',
          params: filters,
        };
      },
    }),

    // CONQUEST MODE
    conquestMode: builder.query<any, { workspace_id?: string; client_id: string, region_name: string }>({
      query: ({ workspace_id, client_id, region_name }) => {
        let url = '/non-branded/conquest-mode/';
        const params: string[] = [];

        if (workspace_id) {
          params.push(`workspace_id=${workspace_id}`);
        }

        return {
          url: url + (params.length > 0 ? `?${params.join('&')}` : ''),
          method: 'POST',
          body: { client_id, region_name },
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        };
      },
    }),
    addConquestMode: builder.mutation<any, { workspace_id: number; data: any[] }>({
      query: ({ workspace_id, data }) => ({
        url: `/non-branded/conquest-mode/add?workspace_id=${workspace_id}`,
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }),
    }),
    disableConquestMode: builder.mutation<
      { success: boolean; disabled_ids: number[] },
      { ids: number[]; workspace_id: number }
    >({
      query: ({ ids, workspace_id }) => ({
        url: `/non-branded/conquest-mode/disable?workspace_id=${workspace_id}`,
        method: 'POST',
        body: ids.map(id => ({ id })),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }),
    }),
    updateConquestMode: builder.mutation<any, { workspace_id: any; data: any[] }>({
      query: ({ workspace_id, data }) => ({
        url: `/non-branded/conquest-mode/update?workspace_id=${workspace_id}`,
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }),
    }),
    regionsConquestMode: builder.query<RegionsResponse, any>({
      query: (query) => {
        let url = '/non-branded/conquest-mode/regions';
        let params: any = [];


        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';

        return {
          url: url + '?' + params.join('&') + workspace_id,
          method: 'GET',
        };
      },
    }),
  }),
});

export const {
  useSearchTermsFiltersQuery,
  useSearchTermsGridQuery,
  useOpportunityCardsQuery,
  useClicksAdCostsQuery,
  useVisibilityQuery,
  useTopCompetQuery,
  useRegionsQuery,
  useCompetitorsQuery,
  useConquestModeQuery,
  useAddConquestModeMutation,
  useDisableConquestModeMutation,
  useUpdateConquestModeMutation,
  useLazyCompetitorsQuery,
  useRegionsConquestModeQuery
} = nonBrandedAPI;
export default nonBrandedAPI;
